import { useSelector } from 'react-redux'

import styles from './YourTrialEndsCallout.module.scss'

import { AddBillingInfoButton } from '@/components/AddBillingInfoButton'
import { Callout } from '@/components/Callout/Callout'
import {
  selectCurrentEnterprise,
  selectGracePeriodDaysLeft,
  selectIsAdmin,
  selectIsInGracePeriod,
  selectSeatInfo,
  selectUserPlanType,
} from '@/store/features/user/userSlice'
import { ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES, PlanType } from '@/types'
import { getPaidPlanLabel } from '@/utils/subscription'

interface YourTrialEndsCalloutProps {
  // Active-trial countdown only. Grace-period messaging uses `selectGracePeriodDaysLeft` from the store and ignores this prop.
  daysLeft?: number
}

// Home callout for trial / grace state. Branches on role: admin-in-grace gets the urgent CTA, non-admin-in-grace gets a softer "ask your admin" nudge, active trial keeps the existing copy.
export const YourTrialEndsCallout = ({ daysLeft }: YourTrialEndsCalloutProps) => {
  const enterprise = useSelector(selectCurrentEnterprise)
  // If enterprise doesn't have subscription, default to Pro
  // This will only happen when we fail to kick users off the enterprise
  // when their trial ends
  const currentPlanType = useSelector(selectUserPlanType) ?? PlanType.Pro
  const seatInfo = useSelector(selectSeatInfo)
  // In-house trials have no Stripe subscription; the tier then lives on seat info (same two-source read as Billing).
  const planLabel =
    getPaidPlanLabel(currentPlanType, enterprise?.subscription?.tier ?? seatInfo?.tier) ?? currentPlanType
  const isActiveOrTrialing = ACTIVE_ENTERPRISE_SUBSCRIPTION_STATUSES.includes(enterprise?.subscription?.status ?? '')
  const isInGracePeriod = useSelector(selectIsInGracePeriod)
  const isAdmin = useSelector(selectIsAdmin)
  const graceDaysLeft = useSelector(selectGracePeriodDaysLeft)

  let title: string
  let description: string
  let showCta: boolean

  if (isInGracePeriod) {
    const dayWord = graceDaysLeft === 1 ? 'day' : 'days'
    title = `Your ${planLabel} trial has ended`
    if (isAdmin) {
      description = `Add billing info within ${graceDaysLeft} ${dayWord} to keep access for you and your team.`
      showCta = true
    } else {
      // Non-admins can't act on billing — soften copy and drop the CTA.
      description = `Your team has ${graceDaysLeft} ${dayWord} of access left. Ask an admin to add billing info to keep ${planLabel} features.`
      showCta = false
    }
  } else if (isActiveOrTrialing) {
    title = daysLeft ? `Your ${planLabel} Trial ends in ${daysLeft} days` : `Your ${planLabel} Trial ends soon`
    description = 'You will be upgraded automatically at the end of your trial.'
    showCta = false
  } else {
    title = daysLeft ? `Your ${planLabel} Trial ends in ${daysLeft} days` : `Your ${planLabel} Trial ends soon`
    description = `Add billing information to keep access to ${planLabel} and your team. Pay $0 today and only get charged when the trial ends.`
    showCta = true
  }

  return (
    <Callout.Root>
      <Callout.Header>
        <Callout.Title>{title}</Callout.Title>
        <Callout.Description>{description}</Callout.Description>
      </Callout.Header>
      {showCta && <AddBillingInfoButton type="dark" title="Add billing info" className={styles.ctaButton} />}
    </Callout.Root>
  )
}
